// LAZY loaded component
import useFrontendBoot from "@/Hooks/useFrontendBoot";
import usePartnerLogin from "@/PartnerPages/Login/hooks/usePartnerLogin";
import { PARTNERS_TABS } from "@/Routes/partnerSidebar.utility";
import FullPageLoader from "@/UI/Loaders/FullPageLoader";
import { stringUtility } from "@repo/utilities/string.utility.ts";
import { lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import Navbar from "../Navbar";
import Sidebar from "../Sidebar";
import SuspenseFallback from "./components/SuspenseFallback";
import PolicyListingIframe from "@/Pages/BrocorePolicy/BrocorePolicy";

const Reports = lazy(() => import("@/Pages/Reports"));
const ProposalBulkUpload = lazy(
  () => import("@/Pages/ProposalBulkUpload/ProposalUpload")
);
const Home = lazy(() => import("@/PartnerPages/Home"));
const Dashboard = lazy(() => import("@/Pages/Dashboard"));
const CashDeposit = lazy(() => import("@/Pages/CashDeposite"));
const ActivityLog = lazy(() => import("@/Pages/Activity"));
const Alerts = lazy(() => import("@/Pages/Alert"));
const AlertTemplate = lazy(() => import("@/Pages/Alert Templates"));
const Combos = lazy(() => import("@/Pages/Combos"));
const Riders = lazy(() => import("@/Pages/Riders"));
const RaterConfig = lazy(() => import("@/Pages/RaterConfig"));
const BlockedAreas = lazy(() => import("@/Pages/Block Areas"));
const Loading = lazy(() => import("@/Pages/Loading"));
const CardType = lazy(() => import("@/Pages/CardType"));
const Cards = lazy(() => import("@/Pages/Cards"));
const PartnerAccount = lazy(() => import("@/PartnerPages/Account"));
const Customer = lazy(() => import("@/PartnerPages/Customer"));
const PartnerProfile = lazy(() => import("@/Pages/Profile"));
const PartnerRoles = lazy(() => import("@/PartnerPages/Roles"));
const PartnerUsers = lazy(() => import("@/PartnerPages/Users/PartnerUsers"));
const JourneyLinksPage = lazy(() => import("@/Pages/CustomerJourneyLink"));
const PolicyFeatures = lazy(() => import("@/Pages/PolicyFeatures"));
const Leads = lazy(() => import("@/Pages/Leads"));
const PartnerRegions = lazy(() => import("@/PartnerPages/PartnerRegions"));
const PartnerLandingPage = lazy(() => import("@/Pages/PartnerLandingPage"));
const OrganizationLandingPage = lazy(
  () => import("@/Pages/PartnerLandingPage")
);
const Products = lazy(() => import("@/Pages/Products"));
const ProductFeatures = lazy(() => import("@/Pages/ProductFeatures"));
const Zones = lazy(() => import("@/PartnerPages/Zones"));
const PermissionsPage = lazy(
  () => import("@/PartnerPages/Permissions/PartnerPermissions")
);
const FeatureCategories = lazy(
  () => import("@/Pages/FeatureCategories/FeatureCategories")
);
const FeatureMaster = lazy(() => import("@/Pages/FeatureMaster/index"));
const Transactions = lazy(() => import("@/Pages/Transactions"));
const PartnerThemes = lazy(() => import("@/PartnerPages/Theme"));
const PartnerGeneralSettings = lazy(
  () => import("@/PartnerPages/PartnerGeneralSettings")
);
const ApiToken = lazy(() => import("@/PartnerPages/ApiToken"));
const Plans = lazy(() => import("@/Pages/Plans"));
const Policies = lazy(() => import("@/Pages/Policies"));
const Certificate = lazy(() => import("@/PartnerPages/Certificates"));
const QCRIframe = lazy(() => import("@/Pages/QMS/QCRIframePage"));
const RFQIframe = lazy(() => import("@/Pages/QMS/RFQIframePage"));
const PlacementSlipIframe = lazy(
  () => import("@/Pages/QMS/PlacementSlipIFrame")
);
const SalesTemplateIFrame = lazy(
  () => import("@/Pages/QMS/SalesTemplateIFrame")
);
type TSuspenseRouteHandlerProps = {
  route: string;
};

const AllPages = ({ route }: TSuspenseRouteHandlerProps) => {
  switch (route.toLowerCase()) {
    case "products":
      return <Products />;
    case "product features":
      return <ProductFeatures />;
    case "dashboard":
      return <Dashboard />;
    case "activity log":
      return <ActivityLog />;
    case "journey links":
      return <JourneyLinksPage />;
    case "landing page":
      return <PartnerLandingPage />;
    case "alerts":
      return <Alerts />;
    case "alert templates":
      return <AlertTemplate />;
    case "block areas":
      return <BlockedAreas />;
    case "rater config":
      return <RaterConfig />;
    case "riders":
      return <Riders />;
    case "combos":
      return <Combos />;
    case "loading":
      return <Loading />;
    case "card types":
      return <CardType />;
    case "cards":
      return <Cards />;
    case "certificates":
      return <Certificate />;
    case "profile":
      return <PartnerProfile />;
    case "cash deposits":
      return <CashDeposit />;
    case "roles":
      return <PartnerRoles />;
    case "users":
      return <PartnerUsers />;
    case "accounts":
      return <PartnerAccount />;
    case "leads":
      return <Leads />;
    case "proposal bulk upload":
      return <ProposalBulkUpload />;
    case "permissions":
      return <PermissionsPage />;
    case "plans":
      return <Plans />;
    case "policy features":
      return <PolicyFeatures />;
    case "policies":
      return <Policies />;
    case "policy listing":
      return <PolicyListingIframe />;
    case "transactions":
      return <Transactions />;
    case "themes":
      return <PartnerThemes />;
    case "general settings":
      return <PartnerGeneralSettings />;
    case "api tokens":
      return <ApiToken />;
    case "customer":
      return <Customer />;
    case "regions":
      return <PartnerRegions />;
    case "zones":
      return <Zones />;
    case "reports":
      return <Reports />;
    case "rfq":
      return <RFQIframe />;
    case "qcr":
      return <QCRIframe />;
    case "placement slip":
      return <PlacementSlipIframe />;
    case "sales placement":
      return <SalesTemplateIFrame />;
    case "feature master":
      return <FeatureMaster />;
    case "feature categories":
      return <FeatureCategories />;
    case "partner landing page":
      return <OrganizationLandingPage />;
    case "home":
    default:
      return <Home />;
  }
};

export default function PartnerSuspenseRouteHandler({
  route,
}: TSuspenseRouteHandlerProps) {
  //CUSTOM-HOOKS
  const { subTab } = useParams();
  const {
    functions: { handlePartnerLogout },
  } = usePartnerLogin();
  const { partnerFrontendBootService } = useFrontendBoot();
  const partnerFrontendBootData = partnerFrontendBootService?.data?.data;

  if (partnerFrontendBootService?.isLoading) {
    return <FullPageLoader />;
  }

  return (
    <div className="h-screen w-full flex bg-primary/10">
      {partnerFrontendBootData && (
        <Helmet>
          <link
            rel="icon"
            type="image/svg+xml"
            href={partnerFrontendBootData?.favicon?.original_url}
          />
          <title>{partnerFrontendBootData?.meta_title}</title>
          <meta
            name="description"
            content={partnerFrontendBootData?.meta_description}
          />
        </Helmet>
      )}

      <Sidebar logoutHandler={handlePartnerLogout} TABS={PARTNERS_TABS} />

      <main className="flex-1 pb-6 flex flex-col overflow-y-auto bg-white rounded-l-3xl  shadow-xl">
        <Navbar TABS={PARTNERS_TABS} />

        <Suspense fallback={<SuspenseFallback />}>
          {subTab ? (
            <AllPages route={stringUtility.removeCharAddSpace(subTab, "-")} />
          ) : (
            <AllPages route={route} />
          )}
        </Suspense>
      </main>
    </div>
  );
}
